import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import setAuthHeader from '../../utils/setAuth';
import { REQUEST_MASTER_USER } from '../../store/user/userActionTypes';
import { useUserMaster } from '../../store/user/reducer';
import ProfileView from './ProfileView';
import style from './header.module.scss';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { clearIsLoggedInCookie } from '../../utils/authCookie';
import { googleLogout } from '@react-oauth/google';
import { useQuery } from '@tanstack/react-query';
interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { pathname, query } = router;
  const navRef = useRef<HTMLDivElement>(null);
  const { masterUser, busy } = useUserMaster();

  // State management
  const [showAccount, setShowAccount] = useState<boolean>(false);
  const [showLinkLast, setShowLinkLast] = useState<boolean>(true);
  const [isMobileShow, setIsMobileShow] = useState<boolean>(false);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [authToken, setAuthToken] = useState<string | null>(null);

  const hamBurgerMenu = (): void => {
    setIsMobileShow(!isMobileShow);
  };

  const handleLogin = (): void => {
    router.push('/login');
    setIsMobileShow(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    clearIsLoggedInCookie();
    googleLogout();
    router.push('/');
    window.location.reload();
  };

  // Effects
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const item = localStorage.getItem('auth-token');
      if (item) {
        setAuthHeader(item);
        setShowLinkLast(false);
        setAuthToken(item);
        dispatch({ type: REQUEST_MASTER_USER });
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setIsMobileShow(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  // Cached so navigating between pages doesn't re-hit /user every time —
  // only the visibility check (based on pathname) needs to re-run.
  const { data: headerUserData, isError: headerUserFetchFailed } = useQuery(
    ['header-user-check', authToken],
    () =>
      axios
        .get(`${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/user`, {
          headers: { Authorization: `${authToken}` },
        })
        .then((response) => response.data),
    { enabled: Boolean(authToken), staleTime: 30_000 }
  );

  useEffect(() => {
    if (!authToken) return;

    if (headerUserFetchFailed) {
      setShowChat(false);
      return;
    }

    if (headerUserData) {
      const userData = headerUserData.data;
      setShowChat(userData && router.pathname !== '/supportchat');
      dispatch({ type: REQUEST_MASTER_USER });
    }
  }, [authToken, headerUserData, headerUserFetchFailed, router.pathname, dispatch]);

  const isSupportChat = router.pathname === '/supportchat';

  return (
    <>
      <header
        className={`${style.headerMainDiv}   ${isSupportChat ? style.headerChat : ''}`}
        role="banner"
        aria-label="Main navigation"
      >
        <div className={style.headerPopupContainer}>
          <div className={style.headerSubDiv}>
            {/* Desktop Navigation */}
            <nav className={style.nav} role="navigation" aria-label="Main navigation">
              <div className={style.options}>
                <Link href="/" aria-label="VxFX Home">
                  <a className={style.logo}>
                    <Image src="/vxfx-app-name-book.svg" alt="VxFX Logo" width={119} height={44} priority />
                  </a>
                </Link>

                <div className={style.links}>
                  <div className={style.linkItem}>
                    <Link href="/">
                      <a>
                        <Typography
                          variant="text-md"
                          as="p"
                          color="primary"
                          weight="normal"
                          className={pathname === '/' ? style.linkStyle : ''}
                        >
                          Home
                        </Typography>
                      </a>
                    </Link>
                  </div>

                  <div className={style.linkItem}>
                    <Link href="/video-plugin-framework/vxfx" prefetch={false}>
                      <a>
                        <Typography
                          variant="text-md"
                          as="p"
                          color="primary"
                          weight="normal"
                          className={
                            pathname.includes('/video-plugin-framework/') ? style.linkStyle : ''
                          }
                        >
                          Product
                        </Typography>
                      </a>
                    </Link>
                  </div>

                  <div className={style.linkItem}>
                    <Link href="/about-us" prefetch={false}>
                      <a>
                        <Typography
                          variant="text-md"
                          as="p"
                          color="primary"
                          weight="normal"
                          className={pathname === '/about-us' ? style.linkStyle : ''}
                        >
                          About Us
                        </Typography>
                      </a>
                    </Link>
                  </div>

                  <div className={style.linkItem}>
                    <Link href="/contact-us" prefetch={false}>
                      <a>
                        <Typography
                          variant="text-md"
                          color="primary"
                          weight="normal"
                          className={pathname === '/contact-us' ? style.linkStyle : ''}
                        >
                          Contact Us
                        </Typography>
                      </a>
                    </Link>
                  </div>
                </div>
              </div>

              {showLinkLast && !masterUser.firstName ? (
                <Button
                  variant="primary"
                  size="md"
                  label="Log In"
                  onClick={handleLogin}
                />
              ) : (
                <div className={style.mainsigninButtonDiv}>
                  <div className={style.subsigninButtonDiv}>
                    {/* AVATAR */}
                    {masterUser.email ? (
                      <div
                        className={style.avatarIcon}
                        onClick={() => {
                          setShowAccount(!showAccount);
                        }}
                      >
                        <img
                          src={masterUser.profileImage?.url || '/avatar.svg'}
                          loading="lazy"
                          alt="Avatar"
                        />
                      </div>
                    ) : (
                      <SkeletonTheme baseColor="#202020" highlightColor="#444" borderRadius={50}>
                        <Skeleton width={40} height={40} />
                      </SkeletonTheme>
                    )}
                    {showAccount && (
                      <ProfileView
                        setIsMobileShow={setIsMobileShow}
                        setShowAccount={setShowAccount}
                      />
                    )}
                  </div>
                </div>
              )}
            </nav>

            {/* Mobile Navigation */}
            <nav
              className={style.mobilenav}
              ref={navRef}
              role="navigation"
              aria-label="Mobile navigation"
            >
              <Link href="/" aria-label="Epen Home">
                <a className={style.logo}>
                  <Image src="/vxfx-app-name-book.svg" alt="VxFX Logo" width={119} height={44} priority />
                </a>
              </Link>

              {isMobileShow && (
                <div className={style.signinResponsiveDiv}>
                  <div className={style.sidebar}>
                    <Link href="/">
                      <a
                        className={style.menuItem}
                        onClick={() => {
                          setIsMobileShow(false);
                        }}
                      >
                        <Typography
                          variant="text-md"
                          color="primary"
                          weight="medium"
                          className={pathname === '/' ? style.linkStyle : ''}
                        >
                          Home
                        </Typography>
                      </a>
                    </Link>

                    {/* Products Section */}
                    <Link href="/video-plugin-framework/vxfx" prefetch={false}>
                      <a
                        className={style.menuItem}
                        onClick={() => {
                          setIsMobileShow(false);
                        }}
                      >
                        <Typography
                          variant="text-md"
                          color="primary"
                          weight="medium"
                          className={
                            pathname.includes('/video-plugin-framework/') ? style.linkStyle : ''
                          }
                        >
                          Product
                        </Typography>
                      </a>
                    </Link>

                    <Link href="/about-us" prefetch={false}>
                      <a
                        className={style.menuItem}
                        onClick={() => {
                          setIsMobileShow(false);
                        }}
                      >
                        <Typography
                          variant="text-md"
                          color="primary"
                          weight="medium"
                          className={pathname === '/about-us' ? style.linkStyle : ''}
                        >
                          About Us
                        </Typography>
                      </a>
                    </Link>

                    <Link href="/contact-us" prefetch={false}>
                      <a
                        className={style.menuItem}
                        onClick={() => {
                          setIsMobileShow(false);
                        }}
                      >
                        <Typography
                          variant="text-md"
                          color="primary"
                          weight="medium"
                          className={pathname === '/contact-us' ? style.linkStyle : ''}
                        >
                          Contact Us
                        </Typography>
                      </a>
                    </Link>

                    {/* Divider */}
                    <div className={style.divider} />

                    {showLinkLast && !masterUser.firstName ? (
                      <Button
                        variant="primary"
                        size="md"
                        label="Log In"
                        fullWidth
                        onClick={handleLogin}
                      />
                    ) : (
                      <>
                        <Link href="/members-area?tab=myaccount">
                          <a
                            className={style.menuItem}
                            onClick={() => {
                              setIsMobileShow(false);
                            }}
                            style={{
                              flexDirection: 'row',
                              gap: '12px',
                              alignItems: 'center',
                            }}
                          >
                            <div className={style.menuIcon}>
                              <Image src="/user.svg" alt="User" width={16} height={16} />
                            </div>
                            <Typography
                              variant="text-md"
                              color="primary"
                              weight="medium"
                              className={
                                pathname === '/members-area?tab=myaccount' ? style.linkStyle : ''
                              }
                            >
                              My Account
                            </Typography>
                          </a>
                        </Link>

                        <Link href="/members-area?tab=myorders">
                          <a
                            className={style.menuItem}
                            onClick={() => {
                              setIsMobileShow(false);
                            }}
                            style={{
                              flexDirection: 'row',
                              gap: '12px',
                              alignItems: 'center',
                            }}
                          >
                            <div className={style.menuIcon}>
                              <Image src="/order.svg" alt="Orders" width={16} height={16} />
                            </div>
                            <Typography
                              variant="text-md"
                              color="primary"
                              weight="medium"
                              className={
                                pathname === '/members-area?tab=myorders' ? style.linkStyle : ''
                              }
                            >
                              My Orders
                            </Typography>
                          </a>
                        </Link>

                        <Link href="/members-area?tab=mysubscriptions">
                          <a
                            className={style.menuItem}
                            onClick={() => {
                              setIsMobileShow(false);
                            }}
                            style={{
                              flexDirection: 'row',
                              gap: '12px',
                              alignItems: 'center',
                            }}
                          >
                            <div className={style.menuIcon}>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.33333 5.33333C1.33333 4.59695 1.93029 4 2.66667 4H13.3333C14.0697 4 14.6667 4.59695 14.6667 5.33333V12C14.6667 12.7364 14.0697 13.3333 13.3333 13.3333H2.66667C1.93029 13.3333 1.33333 12.7364 1.33333 12V5.33333Z"
                                  stroke="#61656C"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M1.33333 6.66667H14.6667"
                                  stroke="#61656C"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M4 9.33333H5.33333M7.33333 9.33333H10.6667"
                                  stroke="#61656C"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 2.66667V4"
                                  stroke="#61656C"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <Typography
                              variant="text-md"
                              color="primary"
                              weight="medium"
                              className={
                                pathname === '/members-area?tab=mysubscriptions'
                                  ? style.linkStyle
                                  : ''
                              }
                            >
                              My Subscriptions
                            </Typography>
                          </a>
                        </Link>

                        <Link href="/downloads">
                          <a
                            className={style.menuItem}
                            onClick={() => {
                              setIsMobileShow(false);
                            }}
                            style={{
                              flexDirection: 'row',
                              gap: '12px',
                              alignItems: 'center',
                            }}
                          >
                            <div className={style.menuIcon}>
                              <Image src="/download-cloud.svg" alt="Downloads" width={16} height={16} />
                            </div>
                            <Typography
                              variant="text-md"
                              color="primary"
                              weight="medium"
                              className={pathname === '/downloads' ? style.linkStyle : ''}
                            >
                              Downloads
                            </Typography>
                          </a>
                        </Link>

                        <Link href="/members-area?tab=myaddresses">
                          <a
                            className={style.menuItem}
                            onClick={() => {
                              setIsMobileShow(false);
                            }}
                            style={{
                              flexDirection: 'row',
                              gap: '12px',
                              alignItems: 'center',
                              marginBottom: '12px',
                            }}
                          >
                            <div className={style.menuIcon}>
                              <Image src="/address.svg" alt="Addresses" width={16} height={16} />
                            </div>
                            <Typography
                              variant="text-md"
                              color="primary"
                              weight="medium"
                              className={
                                pathname === '/members-area?tab=myaddresses' ? style.linkStyle : ''
                              }
                            >
                              My Addresses
                            </Typography>
                          </a>
                        </Link>

                        <div className={style.mainResponsivePopup}>
                          <div className={style.toplogin}>
                            {masterUser.email ? (
                              <div
                                className={style.avatarIcon}
                                style={{ border: 'none', cursor: 'auto' }}
                              >
                                <img
                                  src={masterUser.profileImage?.url || '/avatar.svg'}
                                  loading="lazy"
                                  alt="Avatar"
                                />
                              </div>
                            ) : (
                              <SkeletonTheme
                                baseColor="#202020"
                                highlightColor="#444"
                                borderRadius={50}
                              >
                                <Skeleton width={40} height={40} />
                              </SkeletonTheme>
                            )}
                          </div>
                          <div className={style.topUserEmail}>
                            {masterUser.email ? (
                              <div className={style.topUserEmailDiv}>
                                <Typography variant="text-sm" color="grayDark-50">
                                  {`${masterUser.firstName || '-'} ${masterUser.lastName || ''}`}
                                </Typography>
                                <Typography variant="text-sm" color="grayDark-400">
                                  {masterUser.email}
                                </Typography>
                              </div>
                            ) : (
                              <div className={style.topUserEmailDiv}>
                                <SkeletonTheme
                                  baseColor="#202020"
                                  highlightColor="#444"
                                  borderRadius={50}
                                >
                                  <Skeleton width={100} height={20} />
                                </SkeletonTheme>
                                <SkeletonTheme
                                  baseColor="#202020"
                                  highlightColor="#444"
                                  borderRadius={50}
                                >
                                  <Skeleton width={150} height={20} />
                                </SkeletonTheme>
                              </div>
                            )}
                            <Image
                              src="/logout.svg"
                              alt="Logout"
                              width={16}
                              height={16}
                              onClick={handleLogout}
                              style={{
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* HAMBURGER */}
              <div className={style.hamburger} onClick={hamBurgerMenu}>
                <Image src="/hamburger.svg" alt="Hamburger" width={40} height={40} />
              </div>
            </nav>
          </div>
        </div>

      </header>

      {showChat && (
        <Link href="/supportchat">
          <a className="mainIcon">
            <Image src="/supportChatLogo.png" alt="Support Chat" width={173} height={93} />
          </a>
        </Link>
      )}
    </>
  );
};

export default Header;
